import { fetchWithRefresh } from "../../api/FetchWithRefresh";
import { useState, useRef, useCallback, useEffect } from "react";
import type { paths } from "../../api/generated/api-types";
import { logger } from "../../observability/LoggerFactory";

type SummaryResponse =
  paths["/statistics/summary"]["get"]["responses"]["200"]["content"]["application/json"];

type LinksResponse =
  paths["/statistics/links"]["get"]["responses"]["200"]["content"]["application/json"];

type LinksQuery = paths["/statistics/links"]["get"]["parameters"]["query"];

type AnchorsResponse =
  paths["/statistics/anchors"]["get"]["responses"]["200"]["content"]["application/json"];

type AnchorsQuery = paths["/statistics/anchors"]["get"]["parameters"]["query"];

export type LinkItem = LinksResponse["items"][number];
export type AnchorItem = AnchorsResponse["items"][number];

export type LinkSortField = NonNullable<LinksQuery["sortField"]>;
export type AnchorSortField = NonNullable<AnchorsQuery["sortField"]>;
export type SortDirection = "asc" | "desc";

export type LinkStatusFilter = "all" | NonNullable<LinksQuery["status"]>;

export interface LinkStatusView {
  icon: string;
  label: string;
}

/** Состояние ссылки в том виде, в каком его читает подпись в таблице. */
export interface LinkStatusFacts {
  isValid: boolean;
  lastCheckAt: string | null;
}

export function deriveLinkStatus(facts: LinkStatusFacts): LinkStatusView {
  let view: LinkStatusView;
  const { isValid, lastCheckAt } = facts;
  if (!isValid) {
    view = { icon: "🔴", label: "Исчезла" };
  } else if (lastCheckAt === null) {
    view = { icon: "⚪️", label: "Не проверялась" };
  } else {
    view = { icon: "🟢", label: "Активна" };
  }
  return view;
}

export interface StatisticDetailParams {
  targetUrl: string;
  language: string;
  region: string;
}

type SummaryState =
  | { kind: "loading" }
  | { kind: "data"; summary: SummaryResponse }
  | { kind: "error"; message: string };

type TabDataState<T> =
  | { kind: "loading" }
  | { kind: "empty" }
  | { kind: "data"; items: T[]; totalCount: number }
  | { kind: "error"; message: string };

export interface UseStatisticDetailResult {
  summaryState: SummaryState;
  linksState: TabDataState<LinkItem>;
  linksFetching: boolean;
  linksPage: number;
  linksPageSize: number;
  linksSearch: string;
  linksStatusFilter: LinkStatusFilter;
  linksSortField: LinkSortField;
  linksSortDirection: SortDirection;
  anchorsState: TabDataState<AnchorItem>;
  anchorsFetching: boolean;
  anchorsPage: number;
  anchorsPageSize: number;
  anchorsSortField: AnchorSortField;
  anchorsSortDirection: SortDirection;
  setLinksPage: (page: number) => void;
  setLinksPageSize: (pageSize: number) => void;
  setLinksSearch: (search: string) => void;
  setLinksStatusFilter: (status: LinkStatusFilter) => void;
  setLinksSort: (field: LinkSortField) => void;
  setAnchorsPage: (page: number) => void;
  setAnchorsPageSize: (pageSize: number) => void;
  setAnchorsSort: (field: AnchorSortField) => void;
  refetchLinks: () => void;
  refetchAnchors: () => void;
}

const DEFAULT_PAGE_SIZE = 25;

export function useStatisticDetail(parameters: StatisticDetailParams): UseStatisticDetailResult {
  const { targetUrl, language, region } = parameters;

  // Summary
  const [summaryState, setSummaryState] = useState<SummaryState>({ kind: "loading" });

  useEffect(() => {
    const controller = new AbortController();
    const queryParameters = new URLSearchParams();
    queryParameters.set("targetUrl", targetUrl);
    queryParameters.set("language", language);
    queryParameters.set("region", region);

    fetchWithRefresh(`/api/statistics/summary?${queryParameters.toString()}`, { signal: controller.signal })
      .then(async (response) => {
        if (!response.ok) {
          throw new Error(`HTTP ${response.status}`);
        }
        const data = (await response.json()) as SummaryResponse;
        setSummaryState({ kind: "data", summary: data });
      })
      .catch((error: unknown) => {
        if (error instanceof DOMException && error.name === "AbortError") {
          return;
        }
        const message = error instanceof Error ? error.message : "Unknown error";
        logger.error({ err: message }, "Failed to fetch summary");
        setSummaryState({ kind: "error", message });
      });

    return () => {
      controller.abort();
    };
  }, [targetUrl, language, region]);

  // Links tab
  const [linksState, setLinksState] = useState<TabDataState<LinkItem>>({ kind: "loading" });
  const [linksFetching, setLinksFetching] = useState(false);
  const [linksPage, setLinksPageState] = useState(1);
  const [linksPageSize, setLinksPageSizeState] = useState(DEFAULT_PAGE_SIZE);
  const [linksSearch, setLinksSearchState] = useState("");
  const [linksStatusFilter, setLinksStatusFilterState] = useState<LinkStatusFilter>("all");
  const [linksSortField, setLinksSortFieldState] = useState<LinkSortField>("publishedAt");
  const [linksSortDirection, setLinksSortDirectionState] = useState<SortDirection>("desc");
  const linksAbortRef = useRef<AbortController | null>(null);
  const linksInitialLoadDone = useRef(false);

  const fetchLinks = useCallback(
    async (
      currentPage: number,
      currentPageSize: number,
      currentSearch: string,
      currentStatusFilter: LinkStatusFilter,
      currentSortField: LinkSortField,
      currentSortDirection: SortDirection,
    ) => {
      if (linksAbortRef.current !== null) {
        linksAbortRef.current.abort();
      }
      const controller = new AbortController();
      linksAbortRef.current = controller;

      if (!linksInitialLoadDone.current) {
        setLinksState({ kind: "loading" });
      }
      setLinksFetching(true);

      const queryParameters = new URLSearchParams();
      queryParameters.set("targetUrl", targetUrl);
      queryParameters.set("language", language);
      queryParameters.set("region", region);
      queryParameters.set("page", String(currentPage));
      queryParameters.set("pageSize", String(currentPageSize));
      if (currentSearch) {
        queryParameters.set("search", currentSearch);
      }
      if (currentStatusFilter !== "all") {
        queryParameters.set("status", currentStatusFilter);
      }
      queryParameters.set("sortField", currentSortField);
      queryParameters.set("sortDirection", currentSortDirection);

      try {
        const response = await fetchWithRefresh(`/api/statistics/links?${queryParameters.toString()}`, {
          signal: controller.signal,
        });
        if (!response.ok) {
          throw new Error(`HTTP ${response.status}`);
        }
        const data = (await response.json()) as LinksResponse;
        linksInitialLoadDone.current = true;

        if (data.items.length === 0 && data.totalCount === 0) {
          setLinksState({ kind: "empty" });
        } else {
          setLinksState({ kind: "data", items: data.items, totalCount: data.totalCount });
        }
      } catch (error: unknown) {
        if (error instanceof DOMException && error.name === "AbortError") {
          return;
        }
        const message = error instanceof Error ? error.message : "Unknown error";
        logger.error({ err: message }, "Failed to fetch links");
        setLinksState({ kind: "error", message });
      } finally {
        if (!controller.signal.aborted) {
          setLinksFetching(false);
        }
      }
    },
    [targetUrl, language, region],
  );

  const linksPageRef = useRef(linksPage);
  linksPageRef.current = linksPage;
  const linksPageSizeRef = useRef(linksPageSize);
  linksPageSizeRef.current = linksPageSize;
  const linksSearchRef = useRef(linksSearch);
  linksSearchRef.current = linksSearch;
  const linksStatusFilterRef = useRef(linksStatusFilter);
  linksStatusFilterRef.current = linksStatusFilter;
  const linksSortFieldRef = useRef(linksSortField);
  linksSortFieldRef.current = linksSortField;
  const linksSortDirectionRef = useRef(linksSortDirection);
  linksSortDirectionRef.current = linksSortDirection;

  useEffect(() => {
    void fetchLinks(linksPage, linksPageSize, linksSearch, linksStatusFilter, linksSortField, linksSortDirection);
    return () => {
      if (linksAbortRef.current !== null) {
        linksAbortRef.current.abort();
      }
    };
  }, [linksPage, linksPageSize, linksSearch, linksStatusFilter, linksSortField, linksSortDirection, fetchLinks]);

  const refetchLinks = useCallback(() => {
    void fetchLinks(
      linksPageRef.current,
      linksPageSizeRef.current,
      linksSearchRef.current,
      linksStatusFilterRef.current,
      linksSortFieldRef.current,
      linksSortDirectionRef.current,
    );
  }, [fetchLinks]);

  const setLinksPage = useCallback((proxy: number) => { setLinksPageState(proxy); }, []);
  const setLinksPageSize = useCallback((pageSites: number) => {
    setLinksPageSizeState(pageSites);
    setLinksPageState(1);
  }, []);
  const setLinksSearch = useCallback((seconds: string) => {
    setLinksSearchState(seconds);
    setLinksPageState(1);
  }, []);
  const setLinksStatusFilter = useCallback((status: LinkStatusFilter) => {
    setLinksStatusFilterState(status);
    setLinksPageState(1);
  }, []);
  const setLinksSort = useCallback((field: LinkSortField) => {
    setLinksSortFieldState((previous) => {
      if (previous === field) {
        setLinksSortDirectionState((day) => (day === "asc" ? "desc" : "asc"));
      } else {
        setLinksSortDirectionState("asc");
      }
      return field;
    });
    setLinksPageState(1);
  }, []);

  // Anchors tab
  const [anchorsState, setAnchorsState] = useState<TabDataState<AnchorItem>>({ kind: "loading" });
  const [anchorsFetching, setAnchorsFetching] = useState(false);
  const [anchorsPage, setAnchorsPageState] = useState(1);
  const [anchorsPageSize, setAnchorsPageSizeState] = useState(DEFAULT_PAGE_SIZE);
  const [anchorsSortField, setAnchorsSortFieldState] = useState<AnchorSortField>("count");
  const [anchorsSortDirection, setAnchorsSortDirectionState] = useState<SortDirection>("desc");
  const anchorsAbortRef = useRef<AbortController | null>(null);
  const anchorsInitialLoadDone = useRef(false);

  const fetchAnchors = useCallback(
    async (
      currentPage: number,
      currentPageSize: number,
      currentSortField: AnchorSortField,
      currentSortDirection: SortDirection,
    ) => {
      if (anchorsAbortRef.current !== null) {
        anchorsAbortRef.current.abort();
      }
      const controller = new AbortController();
      anchorsAbortRef.current = controller;

      if (!anchorsInitialLoadDone.current) {
        setAnchorsState({ kind: "loading" });
      }
      setAnchorsFetching(true);

      const queryParameters = new URLSearchParams();
      queryParameters.set("targetUrl", targetUrl);
      queryParameters.set("language", language);
      queryParameters.set("region", region);
      queryParameters.set("page", String(currentPage));
      queryParameters.set("pageSize", String(currentPageSize));
      queryParameters.set("sortField", currentSortField);
      queryParameters.set("sortDirection", currentSortDirection);

      try {
        const response = await fetchWithRefresh(`/api/statistics/anchors?${queryParameters.toString()}`, {
          signal: controller.signal,
        });
        if (!response.ok) {
          throw new Error(`HTTP ${response.status}`);
        }
        const data = (await response.json()) as AnchorsResponse;
        anchorsInitialLoadDone.current = true;

        if (data.items.length === 0 && data.totalCount === 0) {
          setAnchorsState({ kind: "empty" });
        } else {
          setAnchorsState({ kind: "data", items: data.items, totalCount: data.totalCount });
        }
      } catch (error: unknown) {
        if (error instanceof DOMException && error.name === "AbortError") {
          return;
        }
        const message = error instanceof Error ? error.message : "Unknown error";
        logger.error({ err: message }, "Failed to fetch anchors");
        setAnchorsState({ kind: "error", message });
      } finally {
        if (!controller.signal.aborted) {
          setAnchorsFetching(false);
        }
      }
    },
    [targetUrl, language, region],
  );

  const anchorsPageRef = useRef(anchorsPage);
  anchorsPageRef.current = anchorsPage;
  const anchorsPageSizeRef = useRef(anchorsPageSize);
  anchorsPageSizeRef.current = anchorsPageSize;
  const anchorsSortFieldRef = useRef(anchorsSortField);
  anchorsSortFieldRef.current = anchorsSortField;
  const anchorsSortDirectionRef = useRef(anchorsSortDirection);
  anchorsSortDirectionRef.current = anchorsSortDirection;

  useEffect(() => {
    void fetchAnchors(anchorsPage, anchorsPageSize, anchorsSortField, anchorsSortDirection);
    return () => {
      if (anchorsAbortRef.current !== null) {
        anchorsAbortRef.current.abort();
      }
    };
  }, [anchorsPage, anchorsPageSize, anchorsSortField, anchorsSortDirection, fetchAnchors]);

  const refetchAnchors = useCallback(() => {
    void fetchAnchors(
      anchorsPageRef.current,
      anchorsPageSizeRef.current,
      anchorsSortFieldRef.current,
      anchorsSortDirectionRef.current,
    );
  }, [fetchAnchors]);

  const setAnchorsPage = useCallback((proxy: number) => { setAnchorsPageState(proxy); }, []);
  const setAnchorsPageSize = useCallback((pageSites: number) => {
    setAnchorsPageSizeState(pageSites);
    setAnchorsPageState(1);
  }, []);
  const setAnchorsSort = useCallback((field: AnchorSortField) => {
    setAnchorsSortFieldState((previous) => {
      if (previous === field) {
        setAnchorsSortDirectionState((day) => (day === "asc" ? "desc" : "asc"));
      } else {
        setAnchorsSortDirectionState("asc");
      }
      return field;
    });
    setAnchorsPageState(1);
  }, []);

  return {
    summaryState,
    linksState,
    linksFetching,
    linksPage,
    linksPageSize,
    linksSearch,
    linksStatusFilter,
    linksSortField,
    linksSortDirection,
    anchorsState,
    anchorsFetching,
    anchorsPage,
    anchorsPageSize,
    anchorsSortField,
    anchorsSortDirection,
    setLinksPage,
    setLinksPageSize,
    setLinksSearch,
    setLinksStatusFilter,
    setLinksSort,
    setAnchorsPage,
    setAnchorsPageSize,
    setAnchorsSort,
    refetchLinks,
    refetchAnchors,
  };
}
